import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Table,
  Form,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

// Register chartjs elements
ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * CryptoDashboard
 * - 원본 single-file html (btc_mini_dashboard_...23.html) 기능을 React로 완전 이식
 * - UI: React-Bootstrap, 흰색 Card 배경 (KimpInfo 스타일)
 * - Charts: react-chartjs-2
 *
 * 주의: 브라우저 CORS 정책으로 일부 REST 호출이 막힐 수 있습니다.
 *   -> 로컬에서 테스트할 경우 Live Server / 간단 프록시 사용 권장.
 */

const CryptoDashboard = () => {
  // --- state ---
  const [errMsg, setErrMsg] = useState("");

  // prices / funding / OI
  const [lastMark, setLastMark] = useState(null);
  const [lastFundingPct, setLastFundingPct] = useState(NaN);
  const [nextFundingTime, setNextFundingTime] = useState(null);
  const [fundingHistory, setFundingHistory] = useState([]); // numeric %
  const [fundingLabels, setFundingLabels] = useState([]);

  const [oiPoints, setOiPoints] = useState([]); // [{t:Date, v:number}]
  const [markPoints, setMarkPoints] = useState([]); // [{t:Date,v:number}]

  // Liquidations
  const [btcLiqWindowMins, setBtcLiqWindowMins] = useState(60);
  const [allLiqWindowMins, setAllLiqWindowMins] = useState(60);
  const [liqBuckets, setLiqBuckets] = useState([]); // BTCUSDT per-minute buckets
  const [liqAllBuckets, setLiqAllBuckets] = useState([]); // all symbols buckets
  const [tapeEvents, setTapeEvents] = useState([]); // newest first
  const [tapeRows, setTapeRows] = useState(10);

  // kimchi premium
  const K_COINS = ["BTC", "ETH", "SOL", "XRP", "DOGE", "ADA"];
  const [usdkrw, setUsdkrw] = useState(null);
  const [kimchiRows, setKimchiRows] = useState([]);

  // Signals
  const [thLong, setThLong] = useState(0.60);
  const [thShort, setThShort] = useState(0.40);
  const [signalRows, setSignalRows] = useState([]);

  // misc
  const [updatedLabel, setUpdatedLabel] = useState("--");

  // websocket refs
  const wsLiqRef = useRef(null);
  const isUnmountRef = useRef(false);

  // chart data refs (helpful for streaming updates)
  // const fundingChartRef = useRef(null);
  // const oiChartRef = useRef(null);
  // const liqChartRef = useRef(null);
  // const liqAllChartRef = useRef(null);

  // formatting helpers
  const fmt = (v, digits = 2) => {
    if (!isFinite(v)) return "—";
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });
  };
  const toLocalHM = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // -------------------------
  // Helper math functions (from original)
  // -------------------------
  function logistic(x) { return 1 / (1 + Math.exp(-x)); }
  function zFromSample(arr, x) {
    if (!arr || arr.length < 10 || !isFinite(x)) return 0;
    let n = 0, s = 0;
    for (const v of arr) { if (isFinite(v)) { s += +v; n++; } }
    if (!n) return 0;
    const m = s / n; let vv = 0;
    for (const v of arr) { if (isFinite(v)) vv += (v - m) * (v - m); }
    const sd = Math.sqrt(vv / Math.max(1, n)) || 1e-9;
    return (x - m) / sd;
  }
  function arrDelta(points, minutes) {
    if (!points || points.length < 2) return { dv: 0, v0: 0 };
    const cutoff = Date.now() - minutes * 60 * 1000;
    let earlier = points[0];
    for (const p of points) { if (p.t.getTime() >= cutoff) { earlier = p; break; } }
    const latest = points[points.length - 1] || earlier;
    return { dv: (latest.v - earlier.v), v0: earlier.v };
  }

  // Features for signals (adapted)
  const [fundingHistPct, setFundingHistPct] = useState([]); // array of % for zFromSample
  const [__kpHist, set__kpHist] = useState([]);

  const featureFunding = useCallback(() => {
    if (!isFinite(lastFundingPct)) return 0;
    const fr = lastFundingPct; // %
    const dfr = (fundingHistPct.length >= 2) ? fr - fundingHistPct[fundingHistPct.length - 2] : 0;
    const z1 = zFromSample(fundingHistPct, fr);
    const z2 = zFromSample(fundingHistPct, dfr);
    return -z1 - 0.5 * z2; // contrarian
  }, [lastFundingPct, fundingHistPct]);

  const featureOI = useCallback((mins) => {
    if (oiPoints.length < 2 || markPoints.length < 2) return 0;
    const { dv, v0 } = arrDelta(oiPoints, mins);
    const { dv: dp } = arrDelta(markPoints, mins);
    const sign = dp >= 0 ? 1 : -1;
    const norm = v0 > 0 ? dv / Math.max(1, v0) : 0;
    const recent = []; const N = Math.min(oiPoints.length - 1, 30);
    for (let i = 1; i <= N; i++) { const a = oiPoints[oiPoints.length - i], b = oiPoints[oiPoints.length - i - 1] || a; recent.push((a.v - b.v) / Math.max(1, b.v)); }
    const z = zFromSample(recent, norm);
    return z * sign;
  }, [oiPoints, markPoints]);

  const featureLiq = useCallback((mins) => {
    if (!liqBuckets.length) return 0;
    const cutoff = Date.now() - mins * 60 * 1000; let L = 0, S = 0;
    for (const b of liqBuckets) { const ts = new Date(b.minuteISO).getTime(); if (ts >= cutoff) { L += b.longUsd || 0; S += b.shortUsd || 0; } }
    const T = L + S; if (T <= 0) return 0; const I = (L - S) / T; return -I * 1.5;
  }, [liqBuckets]);

  const featureKimchi = useCallback(() => {
    const arr = [...kimchiRows.map(r => r.premUp).filter(isFinite), ...kimchiRows.map(r => r.premBt).filter(isFinite)];
    if (!arr.length) return 0;
    const kp = arr.reduce((a, b) => a + b, 0) / arr.length;
    set__kpHist(prev => { const next = [...prev, kp]; if (next.length > 200) next.shift(); return next; });
    const z = zFromSample(__kpHist, kp);
    return -z;
  }, [kimchiRows, __kpHist]);

  const computeSignalFor = useCallback((mins) => {
    const w = { fund: 0.7, oi: 0.6, liq: 0.5, kp: 0.4 };
    const Xf = featureFunding();
    const Xo = featureOI(mins);
    const Xl = featureLiq(mins);
    const Xk = featureKimchi();
    const S = w.fund * Xf + w.oi * Xo + w.liq * Xl + w.kp * Xk;
    const pUp = logistic(S);
    return { pUp, pDn: 1 - pUp, S, parts: { Xf, Xo, Xl, Xk } };
  }, [featureFunding, featureOI, featureLiq, featureKimchi]);
  const renderSignals = useCallback(() => {
    const horizons = [15, 60, 240];
    const rows = horizons.map(m => {
      const r = computeSignalFor(m);
      const p = r.pUp;
      const act = (p >= thLong) ? 'LONG' : (p <= thShort ? 'SHORT' : 'HOLD');
      const size = Math.min(Math.abs(p - 0.5) * 2, 1);
      return { horiz: m, pUp: p, pDn: 1 - p, act, size, parts: r.parts };
    });
    setSignalRows(rows);
  }, [computeSignalFor, thLong, thShort]);

  // -------------------------
  // Fetchers (REST)
  // -------------------------
  // robust fetch with simple CORS fallback attempt (like original jget)
  async function jget(url) {
    try {
      const r = await fetch(url, { mode: "cors" });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return await r.json();
    } catch (err) {
      // try jina.ai proxy fallback (may be rate-limited) - keep attempt but might fail
      try {
        const prox = 'https://r.jina.ai/http://' + url.replace(/^https?:\/\//, '');
        const text = await fetch(prox).then(x => x.text());
        return JSON.parse(text);
      } catch (e2) {
        throw err;
      }
    }
  }

  async function loadFX() {
    try {
      const j = await jget('https://api.exchangerate.host/latest?base=USD&symbols=KRW');
      if (j && j.rates && j.rates.KRW) { setUsdkrw(+j.rates.KRW); return +j.rates.KRW; }
    } catch (e) { console.warn('FX load failed', e); }
    try {
      const j2 = await jget('https://open.er-api.com/v6/latest/USD');
      if (j2 && j2.rates && j2.rates.KRW) { setUsdkrw(+j2.rates.KRW); return +j2.rates.KRW; }
    } catch (e) { console.warn('FX load failed2', e); }
    return usdkrw || null;
  }

  // Kimchi premium loader (many coins) - adapted from original
  async function loadKimchi() {
    try {
      const fx = await loadFX();
      const binCalls = K_COINS.map(sym => jget('https://api.binance.com/api/v3/ticker/price?symbol=' + sym + 'USDT').catch(() => null));
      const upbitUrl = 'https://api.upbit.com/v1/ticker?markets=' + K_COINS.map(c => 'KRW-' + c).join(',');
      const bithumbUrl = 'https://api.bithumb.com/public/ticker/ALL_KRW';

      const [binArr, upArr, btAll] = await Promise.all([
        Promise.all(binCalls),
        jget(upbitUrl).catch(() => []),
        jget(bithumbUrl).catch(() => null)
      ]);
      const binMap = {}; (binArr || []).forEach(j => { if (j && j.symbol && j.price) binMap[j.symbol.replace('USDT', '')] = +j.price; });
      const upMap = {}; (upArr || []).forEach(o => { const c = (o.market || '').replace('KRW-', ''); if (c) upMap[c] = +o.trade_price; });
      const btMap = {}; if (btAll && btAll.data) { for (const k of K_COINS) { const row = btAll.data[k]; if (row && row.closing_price) btMap[k] = +row.closing_price; } }

      const rows = K_COINS.map(c => {
        const bin = binMap[c] || NaN; const up = upMap[c] || NaN; const bt = btMap[c] || NaN;
        const premUp = (isFinite(up) && isFinite(bin) && fx) ? ((up / fx) / bin - 1) * 100 : NaN;
        const premBt = (isFinite(bt) && isFinite(bin) && fx) ? ((bt / fx) / bin - 1) * 100 : NaN;
        return { c, bin, up, premUp, bt, premBt };
      });

      // sort by premUp desc like original
      rows.sort((a, b) => { const A = isFinite(a.premUp) ? a.premUp : -Infinity; const B = isFinite(b.premUp) ? b.premUp : -Infinity; return B - A; });

      setKimchiRows(rows);
      setUpdatedLabel(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (e) {
      console.error('[Kimchi] load failed', e);
      setErrMsg('김치프리미엄 로드 실패: ' + (e?.message || e));
    }
  }

  // Load mark/funding (single)
  async function loadMarkAndFunding() {
    try {
      const url = 'https://fapi.binance.com/fapi/v1/premiumIndex?symbol=BTCUSDT';
      const j = await jget(url);
      const mark = parseFloat(j.markPrice);
      setLastMark(mark);
      setLastFundingPct(parseFloat(j.lastFundingRate) * 100);
      setNextFundingTime(j.nextFundingTime ? toLocalHM(j.nextFundingTime) : null);
      setUpdatedLabel(new Date().toLocaleString());

      // record mark history
      setMarkPoints(prev => {
        const next = [...prev, { t: new Date(), v: mark }];
        if (next.length > 600) next.shift();
        return next;
      });
    } catch (e) {
      console.warn('loadMarkAndFunding failed', e);
      setErrMsg('Funding/Mark 로드 실패: ' + (e?.message || e));
    }
  }

  async function loadFundingHistory() {
    try {
      const url = 'https://fapi.binance.com/fapi/v1/fundingRate?symbol=BTCUSDT&limit=50';
      const arr = await jget(url);
      const labels = arr.map(x => toLocalHM(x.fundingTime));
      const data = arr.map(x => (parseFloat(x.fundingRate) * 100));
      setFundingLabels(labels.reverse());
      setFundingHistory(data.reverse());
      setFundingHistPct(data.reverse());
    } catch (e) { console.warn('Funding history load failed', e); }
  }

  async function loadOpenInterest() {
    try {
      const url = 'https://fapi.binance.com/fapi/v1/openInterest?symbol=BTCUSDT';
      const j = await jget(url);
      const oiBase = parseFloat(j.openInterest); // in BTC
      const mark = lastMark || parseFloat(j.markPrice || 0);
      const oiUsd = oiBase * (mark || 0);
      setOiPoints(prev => {
        const next = [...prev, { t: new Date(), v: oiUsd }];
        if (next.length > 180) next.shift();
        return next;
      });
    } catch (e) { console.warn('OI load failed', e); }
  }

  // -------------------------
  // Liquidations WS & bucket logic
  // -------------------------
  function bucketKey(ts) {
    const d = new Date(ts);
    d.setSeconds(0, 0);
    return d.toISOString();
  }
  function upsertBucketLocal(buckets, ts, side, notional) {
    const key = bucketKey(ts);
    let b = buckets.find(x => x.minuteISO === key);
    if (!b) { b = { minuteISO: key, longUsd: 0, shortUsd: 0 }; buckets.push(b); }
    if (side === 'BUY') { b.longUsd += notional; } else { b.shortUsd += notional; }
  }

  function pruneOldBucketsLocal(buckets, mins) {
    const cutoff = Date.now() - mins * 60 * 1000;
    return buckets.filter(b => new Date(b.minuteISO).getTime() >= cutoff);
  }

  function startLiqWS() {
    try {
      if (wsLiqRef.current && (wsLiqRef.current.readyState === WebSocket.OPEN || wsLiqRef.current.readyState === WebSocket.CONNECTING)) return;
      const ws = new WebSocket('wss://fstream.binance.com/ws/!forceOrder@arr');
      wsLiqRef.current = ws;

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          const arr = Array.isArray(msg) ? msg : [msg];
          let addedTape = [];
          let buckets = [...liqBuckets];
          let allBuckets = [...liqAllBuckets];

          arr.forEach(m => {
            const o = m.o || m.data || m;
            if (!o) return;
            const sym = (o.s || o.symbol || '').toUpperCase();
            const side = (o.S || o.side || '').toUpperCase() || (o.side ? o.side.toUpperCase() : 'SELL');
            const price = parseFloat(o.p || o.price || 0);
            const qty = parseFloat(o.q || o.origQty || 0);
            const ts = o.T || o.E || Date.now();
            if (!sym || !price || !qty) return;
            const notional = price * qty;
            if (sym === 'BTCUSDT') upsertBucketLocal(buckets, ts, side, notional);
            upsertBucketLocal(allBuckets, ts, side, notional);
            addedTape.push({ t: ts, sym, side, price, qty, notional });
          });

          // update states
          setLiqBuckets(prev => {
            let merged = [...prev, ...buckets.filter(b => !prev.some(p => p.minuteISO === b.minuteISO))];
            // but better to replace by key merge:
            const map = new Map();
            merged.forEach(x => map.set(x.minuteISO, { minuteISO: x.minuteISO, longUsd: (x.longUsd || 0), shortUsd: (x.shortUsd || 0) }));
            // update with new buckets
            buckets.forEach(b => {
              const cur = map.get(b.minuteISO) || { minuteISO: b.minuteISO, longUsd: 0, shortUsd: 0 };
              cur.longUsd = (cur.longUsd || 0) + (b.longUsd || 0);
              cur.shortUsd = (cur.shortUsd || 0) + (b.shortUsd || 0);
              map.set(b.minuteISO, cur);
            });
            const next = Array.from(map.values()).sort((a, b) => a.minuteISO.localeCompare(b.minuteISO));
            return pruneOldBucketsLocal(next, btcLiqWindowMins);
          });

          setLiqAllBuckets(prev => {
            const map = new Map();
            [...prev, ...allBuckets].forEach(x => {
              const cur = map.get(x.minuteISO) || { minuteISO: x.minuteISO, longUsd: 0, shortUsd: 0 };
              cur.longUsd = (cur.longUsd || 0) + (x.longUsd || 0);
              cur.shortUsd = (cur.shortUsd || 0) + (x.shortUsd || 0);
              map.set(x.minuteISO, cur);
            });
            const next = Array.from(map.values()).sort((a, b) => a.minuteISO.localeCompare(b.minuteISO));
            return pruneOldBucketsLocal(next, allLiqWindowMins);
          });

          if (addedTape.length) {
            setTapeEvents(prev => {
              const next = [...addedTape.map(t => ({ ...t })), ...prev];
              if (next.length > 2000) next.length = 2000;
              return next;
            });
          }
        } catch (e) {
          console.warn('WS unified parse', e);
        }
      };

      ws.onerror = (e) => console.warn('WS error', e);
      ws.onclose = () => {
        wsLiqRef.current = null;
        if (!isUnmountRef.current) setTimeout(startLiqWS, 2000);
      };
    } catch (e) {
      console.warn('WS start failed', e);
    }
  }

  // -------------------------
  // Chart data derived values
  // -------------------------
  const fundingChartData = {
    labels: fundingLabels,
    datasets: [
      { label: 'Funding %', data: fundingHistory, backgroundColor: 'rgba(96,165,250,0.3)', borderColor: 'rgba(96,165,250,0.9)', tension: 0.2 }
    ]
  };
  const oiChartData = {
    labels: oiPoints.map(p => p.t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })),
    datasets: [{ label: 'OI (USD)', data: oiPoints.map(p => p.v), borderColor: 'rgba(96, 219, 132, 0.9)', backgroundColor: 'rgba(96,219,132,0.25)', tension: 0.2 }]
  };
  const liqChartData = {
    labels: liqBuckets.map(b => new Date(b.minuteISO).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })),
    datasets: [
      { label: 'Long', data: liqBuckets.map(b => Math.round(b.longUsd)), backgroundColor: 'rgba(34,197,94,0.6)' },
      { label: 'Short', data: liqBuckets.map(b => Math.round(b.shortUsd)), backgroundColor: 'rgba(239,68,68,0.6)' }
    ]
  };
  const liqAllChartData = {
    labels: liqAllBuckets.map(b => new Date(b.minuteISO).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })),
    datasets: [
      { label: 'Long (All)', data: liqAllBuckets.map(b => Math.round(b.longUsd)), backgroundColor: 'rgba(34,197,94,0.5)' },
      { label: 'Short (All)', data: liqAllBuckets.map(b => Math.round(b.shortUsd)), backgroundColor: 'rgba(239,68,68,0.5)' }
    ]
  };

  // -------------------------
  // UI actions
  // -------------------------
  function setBtcWindow(mins) {
    const m = Math.max(1, Math.min(1440, parseInt(mins) || 60));
    setBtcLiqWindowMins(m);
    // prune buckets
    setLiqBuckets(prev => pruneOldBucketsLocal(prev, m));
  }
  function setAllWindow(mins) {
    const m = Math.max(1, Math.min(1440, parseInt(mins) || 60));
    setAllLiqWindowMins(m);
    setLiqAllBuckets(prev => pruneOldBucketsLocal(prev, m));
  }
  function setRows(n) {
    setTapeRows(n);
  }

  // -------------------------
  // Self-tests & run loop
  // -------------------------
  useEffect(() => {
    isUnmountRef.current = false;
    // init sequence
    (async () => {
      try {
        await loadMarkAndFunding();
        await loadFundingHistory();
        await loadOpenInterest();
        await loadKimchi();

        // start WS
        startLiqWS();

        // intervals
        const markTimer = setInterval(loadMarkAndFunding, 10_000);
        const oiTimer = setInterval(loadOpenInterest, 30_000);
        const kimchiTimer = setInterval(loadKimchi, 15_000);
        const fundingTimer = setInterval(loadFundingHistory, 60_000);

        const signalTimer = setInterval(() => { renderSignals(); }, 5000);

        return () => {
          clearInterval(markTimer); clearInterval(oiTimer); clearInterval(kimchiTimer); clearInterval(fundingTimer); clearInterval(signalTimer);
        };
      } catch (e) {
        console.error('init failed', e);
        setErrMsg('초기 로드 실패: ' + (e?.message || e));
      }
    })();

    return () => {
      isUnmountRef.current = true;
      if (wsLiqRef.current) { try { wsLiqRef.current.close(); } catch (e) { } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  // recompute signals when relevant inputs change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { renderSignals(); }, [thLong, thShort, fundingHistPct, oiPoints, liqBuckets, kimchiRows, renderSignals]);

  return (
    <div className="crypto-card-container py-4" style={{ maxWidth: 1200 }}>
      <div className="crypto-card">
        {/* 헤더 */}
        <div className="d-flex align-items-center gap-3 border-bottom pb-3 mb-4">
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#3b82f6', boxShadow: '0 0 12px rgba(59, 130, 246, 0.6)' }} />
          <h4 className="fw-bold m-0" style={{ letterSpacing: '-0.02em' }}>
            실시간 비트코인 종합 대시보드
            <span className="text-muted ms-2 fs-6 fw-normal">(Binance USDⓈ-M Futures)</span>
          </h4>
        </div>

        {/* 에러 박스 */}
        {errMsg && (
          <div className="alert alert-danger py-2 px-3 mb-3 border-start border-4 border-danger rounded-3" style={{ fontSize: '0.88rem' }}>
            {errMsg}
          </div>
        )}

        {/* 요약 통계 정보 바 */}
        <div className="crypto-stats-bar">
          <div className="crypto-stat-pill">
            <span>🪙 BTCUSDT</span>
            <strong>{lastMark ? fmt(lastMark, 2) : '--'}</strong>
          </div>
          <div className="crypto-stat-pill">
            <span>📊 Funding Rate</span>
            <strong className={lastFundingPct >= 0 ? "text-success" : "text-danger"}>
              {isFinite(lastFundingPct) ? (lastFundingPct >= 0 ? '+' : '') + lastFundingPct.toFixed(4) + '%' : '--'}
            </strong>
          </div>
          <div className="crypto-stat-pill">
            <span>⏰ Next Funding</span>
            <strong>{nextFundingTime || '--'}</strong>
          </div>
          <div className="crypto-stat-pill">
            <span>📈 Open Interest</span>
            <strong>{oiPoints.length ? (fmt(oiPoints[oiPoints.length - 1].v / 1e9, 2) + ' B') : '--'}</strong>
          </div>
          <span className="text-secondary ms-auto small" style={{ fontSize: '0.8rem' }}>
            🔄 Last Updated: {updatedLabel}
          </span>
        </div>

        {/* 2단 그리드: 차트 & 청산 현황 */}
        <div className="crypto-grid-2 mb-4">
          {/* 왼쪽: 가격 및 차트 */}
          <div className="border rounded-4 p-4 bg-white" style={{ border: '1px solid #edf2f7' }}>
            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ fontSize: '1rem', color: '#334155' }}>
              📈 시세 · 펀딩비 · 미결제약정(OI) 추이
            </h5>
            <div className="row g-4">
              <div className="col-12 col-md-6">
                <div className="text-muted mb-2 small fw-bold">과거 펀딩비 내역 (%)</div>
                <div style={{ height: 220 }}>
                  <Line data={fundingChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { ticks: { callback: v => v + '%' } } } }} />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="text-muted mb-2 small fw-bold">실시간 OI 추이 (USD)</div>
                <div style={{ height: 220 }}>
                  <Line data={oiChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { ticks: { callback: v => v >= 1e9 ? (v / 1e9).toFixed(1) + 'B' : v } } } }} />
                </div>
              </div>
            </div>
          </div>

          {/* 오른쪽: 실시간 강제청산 (BTC) */}
          <div className="border rounded-4 p-4 bg-white" style={{ border: '1px solid #edf2f7' }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h5 className="fw-bold m-0" style={{ fontSize: '1rem', color: '#334155' }}>
                🔥 실시간 강제 청산 (BTCUSDT)
              </h5>
              <Form.Select size="sm" value={btcLiqWindowMins} onChange={(e) => setBtcWindow(e.target.value)} style={{ width: 110, borderRadius: '8px' }}>
                <option value="15">15분</option>
                <option value="30">30분</option>
                <option value="60">1시간</option>
                <option value="240">4시간</option>
                <option value="480">8시간</option>
                <option value="720">12시간</option>
                <option value="1440">24시간</option>
              </Form.Select>
            </div>
            <div className="d-flex gap-2 align-items-center flex-wrap mb-3" style={{ fontSize: '0.85rem' }}>
              <span className="text-muted">누적 청산액:</span>
              <strong className="font-monospace" style={{ fontSize: '0.95rem' }}>{fmt(liqBuckets.reduce((s, b) => s + (b.longUsd || 0) + (b.shortUsd || 0), 0))} USD</strong>
              <span className="crypto-badge-long ms-2">Long: {fmt(liqBuckets.reduce((s, b) => s + (b.longUsd || 0), 0))}</span>
              <span className="crypto-badge-short">Short: {fmt(liqBuckets.reduce((s, b) => s + (b.shortUsd || 0), 0))}</span>
            </div>
            <div style={{ height: 220 }}>
              <Bar data={liqChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { stacked: true }, y: { stacked: true, ticks: { callback: v => v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v } } } }} />
            </div>
          </div>
        </div>

        {/* 전종목 통합 강제 청산 */}
        <div className="border rounded-4 p-4 bg-white mb-4" style={{ border: '1px solid #edf2f7' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="fw-bold m-0" style={{ fontSize: '1rem', color: '#334155' }}>
              🌐 전종목 통합 강제 청산 (All Symbols)
            </h5>
            <Form.Select size="sm" value={allLiqWindowMins} onChange={(e) => setAllWindow(e.target.value)} style={{ width: 110, borderRadius: '8px' }}>
              <option value="15">15분</option>
              <option value="30">30분</option>
              <option value="60">1시간</option>
              <option value="240">4시간</option>
              <option value="480">8시간</option>
              <option value="720">12시간</option>
              <option value="1440">24시간</option>
            </Form.Select>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap mb-3" style={{ fontSize: '0.85rem' }}>
            <span className="text-muted">누적 청산액:</span>
            <strong className="font-monospace" style={{ fontSize: '0.95rem' }}>{fmt(liqAllBuckets.reduce((s, b) => s + (b.longUsd || 0) + (b.shortUsd || 0), 0))} USD</strong>
            <span className="crypto-badge-long ms-2">Long: {fmt(liqAllBuckets.reduce((s, b) => s + (b.longUsd || 0), 0))}</span>
            <span className="crypto-badge-short">Short: {fmt(liqAllBuckets.reduce((s, b) => s + (b.shortUsd || 0), 0))}</span>
          </div>
          <div style={{ height: 200 }}>
            <Bar data={liqAllChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { stacked: true }, y: { stacked: true, ticks: { callback: v => v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v } } } }} />
          </div>
        </div>

        {/* 김치 프리미엄 */}
        <div className="border rounded-4 p-4 bg-white mb-4" style={{ border: '1px solid #edf2f7' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 border-bottom pb-2">
            <h5 className="fw-bold m-0" style={{ fontSize: '1rem', color: '#334155' }}>
              🇰🇷 주요 코인 김치 프리미엄
            </h5>
            <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
              기준 환율: <strong className="text-dark">{usdkrw ? fmt(usdkrw, 2) : '—'} KRW</strong>
            </div>
          </div>
          <div className="crypto-table-wrapper">
            <Table responsive hover className="crypto-table">
              <thead>
                <tr>
                  <th>코인</th>
                  <th className="text-end">Binance (USDT)</th>
                  <th className="text-end">Upbit (KRW)</th>
                  <th className="text-end">Upbit Kimchi</th>
                  <th className="text-end">Bithumb (KRW)</th>
                  <th className="text-end">Bithumb Kimchi</th>
                </tr>
              </thead>
              <tbody>
                {kimchiRows.map(r => (
                  <tr key={r.c}>
                    <td className="fw-bold">{r.c}</td>
                    <td className="text-end font-monospace">{isFinite(r.bin) ? r.bin.toLocaleString() : '—'}</td>
                    <td className="text-end font-monospace">{isFinite(r.up) ? r.up.toLocaleString() : '—'}</td>
                    <td className="text-end">
                      {isFinite(r.premUp) ? (
                        <span className={`crypto-premium-capsule ${r.premUp >= 0 ? 'positive' : 'negative'}`}>
                          {r.premUp.toFixed(2)}%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="text-end font-monospace">{isFinite(r.bt) ? r.bt.toLocaleString() : '—'}</td>
                    <td className="text-end">
                      {isFinite(r.premBt) ? (
                        <span className={`crypto-premium-capsule ${r.premBt >= 0 ? 'positive' : 'negative'}`}>
                          {r.premBt.toFixed(2)}%
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>

        {/* 실시간 강제청산 레코더 */}
        <div className="border rounded-4 p-4 bg-white mb-4" style={{ border: '1px solid #edf2f7' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="fw-bold m-0" style={{ fontSize: '1rem', color: '#334155' }}>
              📜 실시간 강제청산 레코더 (All Symbols)
            </h5>
            <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
              <span>표시 라인:</span>
              <Form.Select size="sm" value={tapeRows} onChange={(e) => setRows(parseInt(e.target.value) || 10)} style={{ width: 80, borderRadius: '6px' }}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
            </div>
          </div>
          <div className="crypto-table-wrapper" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <Table responsive hover className="crypto-table">
              <thead>
                <tr>
                  <th>시간</th>
                  <th>심볼</th>
                  <th>종류</th>
                  <th className="text-end">청산가</th>
                  <th className="text-end">수량</th>
                  <th className="text-end">명목 가치</th>
                </tr>
              </thead>
              <tbody>
                {tapeEvents.slice(0, tapeRows).map((e, idx) => (
                  <tr key={idx}>
                    <td className="text-muted font-monospace">{new Date(e.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                    <td className="fw-bold">{e.sym}</td>
                    <td>
                      <span className={e.side === 'BUY' ? 'crypto-badge-long' : 'crypto-badge-short'}>
                        {e.side === 'BUY' ? 'LONG LIQ' : 'SHORT LIQ'}
                      </span>
                    </td>
                    <td className="text-end font-monospace">{fmt(e.price, 2)}</td>
                    <td className="text-end font-monospace">{fmt(e.qty, 4)}</td>
                    <td className="text-end font-monospace fw-bold text-primary">
                      {e.notional >= 1e6 ? (e.notional / 1e6).toFixed(2) + 'M' : Math.round(e.notional).toLocaleString()} USD
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>

        {/* 시그널 */}
        <div className="border rounded-4 p-4 bg-white mb-4" style={{ border: '1px solid #edf2f7' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 border-bottom pb-2">
            <h5 className="fw-bold m-0" style={{ fontSize: '1rem', color: '#334155' }}>
              💡 AI/통계 트레이딩 시그널 추천
            </h5>
            <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '0.85rem' }}>
              <span>진입 장벽:</span>
              <span>롱 ≥</span>
              <Form.Control size="sm" type="number" step="0.01" value={thLong} onChange={(e) => setThLong(parseFloat(e.target.value) || 0)} style={{ width: 70, borderRadius: '6px' }} />
              <span>숏 ≤</span>
              <Form.Control size="sm" type="number" step="0.01" value={thShort} onChange={(e) => setThShort(parseFloat(e.target.value) || 0)} style={{ width: 70, borderRadius: '6px' }} />
            </div>
          </div>
          <div className="crypto-table-wrapper">
            <Table responsive hover className="crypto-table">
              <thead>
                <tr>
                  <th>지평</th>
                  <th className="text-end">P(상승 확률)</th>
                  <th className="text-end">P(하락 확률)</th>
                  <th className="text-center">추천 포지션</th>
                  <th className="text-end">비중</th>
                </tr>
              </thead>
              <tbody>
                {signalRows.map(r => (
                  <tr key={r.horiz}>
                    <td className="fw-bold">{r.horiz === 240 ? '4시간 (4h)' : r.horiz + '분 (m)'}</td>
                    <td className="text-end font-monospace text-success">{(r.pUp * 100).toFixed(1)}%</td>
                    <td className="text-end font-monospace text-danger">{(r.pDn * 100).toFixed(1)}%</td>
                    <td className="text-center">
                      <span className={r.act === 'LONG' ? 'crypto-badge-long' : r.act === 'SHORT' ? 'crypto-badge-short' : 'crypto-badge-neutral'}>
                        {r.act}
                      </span>
                    </td>
                    <td className="text-end font-monospace fw-bold">{Math.round(r.size * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>

        {/* 팁과 활용법 */}
        <div className="rounded-4 p-3 bg-light" style={{ fontSize: '0.82rem', color: '#64748b' }}>
          <div className="fw-bold text-dark mb-1">💡 대시보드 사용 팁</div>
          <ul className="m-0 ps-3">
            <li>Funding/Mark/OI는 REST 주기폴링(10~30초), Liquidations는 WebSocket 실시간 수집 방식입니다.</li>
            <li>OI(미결제약정)는 현재값을 누적 매핑하여 트렌드를 가늠할 수 있게 해줍니다.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export { CryptoDashboard };